import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Modal from '@/components/modal/Modal';
import ColorSquareButton from '@/components/Button/ColorSquareButton';
import WhiteSquareButton from '@/components/Button/WhiteSquareButton';
import { colors, semanticColors } from '@/lib/colors';
import { useLockerSlot, useSetLockerSlot } from '@/features/locker/hooks';
import { getLockerSlot } from '@/features/locker/api';
import { lockerKeys } from '@/features/locker/keys';
import { buildSlotUpdateBody } from '@/features/locker/lockerDisplay';
import { getMutationErrorMessage } from '@/features/locker/mutationError';
import { LockerLoading, LockerError } from './LockerStates';

/**
 * 칸 고정 비밀번호 설정 모달.
 *
 * 이 화면의 위험은 「고정값으로 통일」이다. 그 칸 이용권들의 **개별 비밀번호를 영구 삭제**한다.
 * 그래서
 *   - 기본 선택은 언제나 「유지」다.
 *   - 확인 직전에 개별값 건수를 **다시 읽는다**. 모달을 열어 둔 사이 다른 관리자가 배정하면
 *     처음 읽은 N 은 이미 옛 숫자다. 지워질 건수를 틀리게 보여주면 확인의 의미가 없다.
 *   - 결과를 성공 한 줄로 뭉개지 않는다. 탈퇴 회원이라 건너뛴 건이 있으면 부분 완료로 말한다.
 */

type UnifyMode = 'keep' | 'unify';

const RadioCard = ({
    checked,
    disabled,
    title,
    description,
    onSelect,
}: {
    checked: boolean;
    disabled?: boolean;
    title: string;
    description: string;
    onSelect: () => void;
}) => (
    <button
        type="button"
        role="radio"
        aria-checked={checked}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onClick={onSelect}
        style={{
            textAlign: 'left',
            width: '100%',
            padding: '12px 14px',
            borderRadius: 10,
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: disabled ? colors.GREY : checked ? colors.BLUE_50 : colors.white,
            // 선택을 색만으로 알리면 색각 이상에서 구분되지 않는다. 굵기·그림자·표식을 함께 쓴다.
            border: `1px solid ${checked && !disabled ? colors.PRIMARY_BLUE : colors.WHITE_600}`,
            boxShadow: checked && !disabled ? 'inset 0 0 0 1px ' + colors.PRIMARY_BLUE : 'none',
            opacity: disabled ? 0.6 : 1,
        }}
    >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
                aria-hidden="true"
                style={{
                    width: 16,
                    height: 16,
                    borderRadius: 999,
                    flex: 'none',
                    border: `2px solid ${checked && !disabled ? colors.PRIMARY_BLUE : colors.GREY_100}`,
                    backgroundColor: checked && !disabled ? colors.PRIMARY_BLUE : colors.white,
                    boxShadow: checked && !disabled ? `inset 0 0 0 3px ${colors.white}` : 'none',
                }}
            />
            <span style={{ fontSize: 14, fontWeight: 700, color: disabled ? colors.GREY_300 : colors.LAVEL_4 }}>{title}</span>
        </span>
        <span
            style={{
                display: 'block',
                marginTop: 6,
                marginLeft: 24,
                fontSize: 12,
                lineHeight: '18px',
                color: disabled ? colors.GREY_300 : colors.LAVEL_1,
            }}
        >
            {description}
        </span>
    </button>
);

export default function LockerSlotPasswordModal({
    centerId,
    zoneId,
    lockerNumber,
    onClose,
    createAlert,
    createTwoButtonAlert,
}: {
    centerId: string;
    zoneId: string;
    lockerNumber: number;
    onClose: () => void;
    createAlert?: any;
    createTwoButtonAlert?: any;
}) {
    const qc = useQueryClient();
    const { data, isLoading, isError, refetch } = useLockerSlot({ centerId, zoneId, lockerNumber });
    const setSlotMutation = useSetLockerSlot();

    /**
     * 입력값은 **서버 값에서 파생**시킨다(사용자가 손대기 전까지).
     *
     * 예전에는 useEffect 로 `setDraft(서버값)` 을 했는데, 그 한 번의 상태 왕복이 반영되지
     * 않으면 입력이 빈 채로 남고 — 빈 값은 이 화면에서 **해제(파괴)** 를 뜻하므로 —
     * 저장을 누르는 순간 멀쩡한 고정값이 지워진다. 파생값은 그 왕복 자체가 없다.
     */
    const [draft, setDraft] = useState('');
    const [touched, setTouched] = useState(false);
    const [unifyMode, setUnifyMode] = useState<UnifyMode>('keep');

    /*
     * 다이얼로그 시맨틱을 **이 모달 안에서** 처리한다.
     *
     * 공용 `components/modal/Modal.tsx` 는 role="dialog"·aria-modal·포커스 트랩이 없지만,
     * 78개 파일이 그것을 쓰고 있어 공용 컴포넌트를 고치면 영향 범위가 너무 넓다.
     * ESC 닫기는 Modal 이 이미 해 주므로 여기서는 나머지만 얹는다.
     */
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const titleId = `locker-slot-title-${lockerNumber}`;

    // 값이 도착해 입력이 그려진 뒤에 포커스를 준다.
    useEffect(() => {
        if (isLoading || isError) return;
        const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
        return () => window.clearTimeout(timer);
    }, [isLoading, isError]);

    const handleDialogKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Tab') return;
        const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
            'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;

        if (event.shiftKey && active === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    }, []);

    const original = data?.slot?.defaultLockNumber ?? '';
    const individualCount = data?.individualLockNumberCount ?? 0;

    // 손대기 전에는 서버 값을 그대로 보여준다. 손댄 뒤에는 재조회가 입력을 덮지 않는다.
    const value = touched ? draft : original;

    const trimmed = value.trim();
    const isClearing = trimmed === '' && original !== '';
    // 고정값이 없는 채로 통일하면 그 칸 비밀번호를 전부 지우기만 한다. 서버도 400 으로 막는다.
    const canUnify = trimmed !== '';

    useEffect(() => {
        if (!canUnify && unifyMode === 'unify') setUnifyMode('keep');
    }, [canUnify, unifyMode]);

    const submit = (unify: boolean) => {
        setSlotMutation.mutate(
            {
                centerId,
                zoneId,
                lockerNumber,
                body: buildSlotUpdateBody({ defaultLockNumber: draft, unifyExisting: unify }),
            },
            {
                onSuccess: (res) => {
                    const result = res.data;
                    if (!result?.success) {
                        createAlert?.('오류', '고정 비밀번호를 저장하지 못했습니다.', true);
                        return;
                    }

                    if (unify) {
                        const { unifiedCount, skippedWithdrawnCount } = result;
                        /*
                         * 서버가 통일 **직후에 다시 센** 남은 개별값 수다. 0 이 아니면 그 사이
                         * 새 배정이 개별값을 넣었다는 뜻이라, "N건 통일 완료"로 끝내면 거짓말이 된다.
                         */
                        const remaining = result.individualLockNumberCount ?? 0;
                        const remainingNote =
                            remaining > 0
                                ? ` 다만 이 칸에는 아직 개별 비밀번호 ${remaining}건이 남아 있습니다(통일 도중 새로 생겼거나 제외된 건입니다).`
                                : '';
                        if (unifiedCount === 0 && skippedWithdrawnCount === 0) {
                            // 통일을 골랐는데 아무것도 바뀌지 않았다. 성공처럼 보이면 안 된다.
                            createAlert?.(
                                '통일할 개별 비밀번호가 없었습니다',
                                `고정 비밀번호는 ${result.slot.defaultLockNumber ?? '없음'}(으)로 저장했습니다. 지울 개별 비밀번호는 남아 있지 않았습니다.${remainingNote}`,
                                true,
                            );
                        } else if (skippedWithdrawnCount > 0) {
                            createAlert?.(
                                '일부만 통일했습니다',
                                `${unifiedCount}건을 통일했고, ${skippedWithdrawnCount}건은 탈퇴 회원이라 제외했습니다. 제외된 기록은 개별 비밀번호가 그대로 남습니다.${remainingNote}`,
                                true,
                            );
                        } else if (remaining > 0) {
                            createAlert?.(
                                '통일했지만 개별 비밀번호가 남아 있습니다',
                                `${unifiedCount}건을 통일했습니다.${remainingNote}`,
                                true,
                            );
                        } else {
                            createAlert?.('알림', `개별 비밀번호 ${unifiedCount}건을 고정값으로 통일했습니다.`, false);
                        }
                    } else if (isClearing) {
                        createAlert?.('알림', '이 칸의 고정 비밀번호를 해제했습니다.', false);
                    } else {
                        createAlert?.('알림', '이 칸의 고정 비밀번호를 저장했습니다.', false);
                    }
                    onClose();
                },
                onError: (error) =>
                    createAlert?.('오류', getMutationErrorMessage(error, '고정 비밀번호 저장에 실패했습니다.'), true),
            },
        );
    };

    const handleSave = async () => {
        if (unifyMode === 'unify') {
            /*
             * 확인 직전에 최신 건수를 다시 읽는다.
             * 모달을 열어 둔 사이 다른 관리자가 이 칸에 배정했다면 처음 읽은 N 은 옛 숫자다.
             * 실패해도 저장 자체를 막지는 않고, 처음 읽은 값으로 물어본다.
             */
            let latestCount: number;
            try {
                const fresh = await qc.fetchQuery({
                    queryKey: lockerKeys.slot(centerId, zoneId, lockerNumber),
                    queryFn: () => getLockerSlot({ centerId, zoneId, lockerNumber }),
                    /*
                     * ⚠️ staleTime 을 반드시 0 으로 눌러야 한다.
                     * 전역 기본값이 1분(lib/queryClient.ts)이라 그냥 부르면 모달을 연 지
                     * 1분이 안 됐을 때 **캐시를 그대로 돌려주고 서버에 가지 않는다.**
                     * 그러면 "확인 직전에 최신 건수를 다시 읽는다"가 아무 일도 하지 않게 된다.
                     */
                    staleTime: 0,
                });
                const fetched = fresh?.data?.individualLockNumberCount;
                if (typeof fetched !== 'number') throw new Error('건수를 확인하지 못했습니다.');
                latestCount = fetched;
            } catch (error) {
                /*
                 * 최신 건수를 못 읽으면 **파괴 작업을 중단한다.**
                 * 지워질 건수를 모르는 채로 "N건이 영구 삭제됩니다"를 물을 수 없고,
                 * 물어보지 않고 지울 수도 없다. 저장 자체를 막고 다시 시도하게 한다.
                 */
                createAlert?.(
                    '지금은 통일할 수 없습니다',
                    getMutationErrorMessage(error, '이 칸의 최신 상태를 확인하지 못했습니다.') +
                        ' 잠시 후 다시 시도해 주세요.',
                    true,
                );
                return;
            }

            if (latestCount === 0) {
                createAlert?.(
                    '통일할 개별 비밀번호가 없습니다',
                    '이 칸에는 개별 비밀번호를 가진 이용권이 없습니다. 고정 비밀번호만 저장합니다.',
                    true,
                    '확인',
                    () => submit(false),
                );
                return;
            }

            // 확인 수단이 없으면 **개별값을 지우지 않는다.** 확인 없는 파괴 경로를 남기지 않는다.
            if (!createTwoButtonAlert) {
                createAlert?.('확인이 필요합니다', '지금은 통일을 진행할 수 없습니다. 화면을 새로고침한 뒤 다시 시도해 주세요.', true);
                return;
            }
            createTwoButtonAlert(
                '개별 비밀번호를 지우고 통일할까요?',
                `개별 비밀번호 ${latestCount}건이 영구 삭제됩니다. 되돌릴 수 없습니다.\n통일 후 이 칸의 비밀번호는 「${trimmed}」 하나가 됩니다.`,
                true,
                '취소',
                '통일하기',
                () => {},
                () => submit(true),
            );
            return;
        }

        if (isClearing) {
            // 해제도 되돌릴 수 없다. 확인 없이 실행하지 않는다.
            if (!createTwoButtonAlert) {
                createAlert?.('확인이 필요합니다', '지금은 해제를 진행할 수 없습니다. 화면을 새로고침한 뒤 다시 시도해 주세요.', true);
                return;
            }
            createTwoButtonAlert(
                '고정 비밀번호를 해제할까요?',
                '이 칸의 고정 비밀번호가 사라집니다. 개별 비밀번호가 없는 회원은 바로 비밀번호 없음 상태가 됩니다.',
                true,
                '취소',
                '해제하기',
                () => {},
                () => submit(false),
            );
            return;
        }

        submit(false);
    };

    const isDirty = trimmed !== original || unifyMode === 'unify';

    return (
        <Modal whiteViewWidth={520} isWhiteOverFlowScroll={false} onClose={onClose} setIsVisible={undefined}>
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onKeyDown={handleDialogKeyDown}
                style={{ display: 'flex', flexDirection: 'column' }}
            >
                <div style={{ padding: '20px 24px', borderBottom: `1px solid ${colors.WHITE_600}` }}>
                    <div id={titleId} style={{ fontSize: 18, fontWeight: 700, color: colors.LAVEL_4 }}>
                        {lockerNumber}번 칸 고정 비밀번호
                    </div>
                    <div style={{ fontSize: 12, color: colors.LAVEL_1, marginTop: 4, lineHeight: '18px' }}>
                        회원이 아니라 <b>자리</b>에 붙는 비밀번호입니다. 이 칸을 쓰는 회원에게 공통으로 적용되고, 회수해도 남습니다.
                    </div>
                </div>

                <div style={{ padding: 24 }}>
                    {isLoading && <LockerLoading cardCount={0} />}
                    {isError && !isLoading && (
                        <LockerError title="칸 정보를 불러오지 못했습니다" onRetry={() => refetch()} />
                    )}

                    {!isLoading && !isError && (
                        <>
                            <label
                                htmlFor={`locker-slot-password-${lockerNumber}`}
                                style={{ display: 'block', fontSize: 13, fontWeight: 700, color: colors.LAVEL_4, marginBottom: 8 }}
                            >
                                고정 비밀번호
                            </label>
                            <input
                                ref={inputRef}
                                id={`locker-slot-password-${lockerNumber}`}
                                // 선행 0(0007)이 보존돼야 한다. number 로 두면 7 이 된다.
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                placeholder="비워 두면 해제됩니다"
                                value={value}
                                onChange={(event) => {
                                    setDraft(event.target.value);
                                    setTouched(true);
                                }}
                                style={{
                                    width: '100%',
                                    padding: '11px 16px',
                                    border: `1px solid ${colors.WHITE_600}`,
                                    borderRadius: 10,
                                    fontSize: 15,
                                    letterSpacing: 1,
                                    outline: 'none',
                                    color: colors.LAVEL_4,
                                    boxSizing: 'border-box',
                                }}
                            />
                            <div style={{ fontSize: 11, color: colors.LAVEL_1, marginTop: 6 }}>
                                앞자리 0 도 그대로 저장됩니다. 최대 32자.
                            </div>

                            {individualCount > 0 && (
                                <div style={{ marginTop: 20 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: colors.LAVEL_4, marginBottom: 4 }}>
                                        이 칸에 개별 비밀번호를 쓰는 이용권 {individualCount}건
                                    </div>
                                    <div style={{ fontSize: 12, color: colors.LAVEL_1, marginBottom: 10, lineHeight: '18px' }}>
                                        개별 비밀번호는 고정값보다 우선합니다. 그대로 두면 그 회원들에게는 고정값이 적용되지
                                        않습니다.
                                    </div>
                                    <div
                                        role="radiogroup"
                                        aria-label="기존 개별 비밀번호 처리 방법"
                                        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                                    >
                                        <RadioCard
                                            checked={unifyMode === 'keep'}
                                            title="기존 개별 비밀번호 유지"
                                            description="지금 값을 그대로 둡니다. 아무것도 삭제하지 않습니다."
                                            onSelect={() => setUnifyMode('keep')}
                                        />
                                        <RadioCard
                                            checked={unifyMode === 'unify'}
                                            disabled={!canUnify}
                                            title="고정값으로 통일"
                                            description={
                                                canUnify
                                                    ? `개별 비밀번호 ${individualCount}건을 지우고 고정값 하나로 맞춥니다. 되돌릴 수 없습니다.`
                                                    : '고정 비밀번호를 먼저 입력해야 선택할 수 있습니다.'
                                            }
                                            onSelect={() => setUnifyMode('unify')}
                                        />
                                    </div>
                                </div>
                            )}

                            {unifyMode === 'unify' && (
                                <div
                                    role="note"
                                    style={{
                                        marginTop: 14,
                                        padding: '11px 14px',
                                        borderRadius: 10,
                                        fontSize: 12,
                                        lineHeight: '18px',
                                        backgroundColor: semanticColors.danger.bg,
                                        color: semanticColors.danger.text,
                                        border: `1px solid ${semanticColors.danger.border}`,
                                    }}
                                >
                                    저장하면 개별 비밀번호가 <b>영구 삭제</b>됩니다. 저장 전에 한 번 더 확인합니다.
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div
                    style={{
                        padding: '16px 24px',
                        borderTop: `1px solid ${colors.WHITE_600}`,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 8,
                    }}
                >
                    <WhiteSquareButton text="취소" size="middle" callBack={onClose} />
                    <ColorSquareButton
                        text="저장"
                        size="middle"
                        isDisabled={isLoading || isError || !isDirty || setSlotMutation.isLoading}
                        callBack={handleSave}
                    />
                </div>
            </div>
        </Modal>
    );
}
